import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { addDays, format } from 'date-fns'
import { Button } from '@cannect/new-components/atoms'
import { usePauseSubscription } from '@cannect/services/resources/recurrency/hooks'
import { formatApiDate } from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { DateField } from './DateField'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.pause

type Props = { detail: SubscriptionDetail; open: boolean; onOpenChange: (open: boolean) => void }

export const PauseDialog = ({ detail, open, onOpenChange }: Props) => {
  const min = format(addDays(new Date(), 1), 'yyyy-MM-dd')
  const max = detail.maxPauseUntil || ''
  const rangeMessage = copy.range(formatApiDate(min), formatApiDate(max))
  const schema = z.object({
    until: z
      .string()
      .min(1, copy.required)
      .refine((value) => value >= min && (!max || value <= max), rangeMessage)
  })
  const form = useForm<{ until: string }>({ resolver: zodResolver(schema), defaultValues: { until: '' } })

  useEffect(() => {
    if (!open) form.reset({ until: '' })
  }, [open])

  const action = useSubscriptionAction({
    detail,
    mutation: usePauseSubscription(detail.id),
    event: 'recurrency_pause',
    fieldErrorCodes: ['PAUSE_LIMIT'],
    onFieldError: (err) => form.setError('until', { message: err.message })
  })

  const onSubmit = form.handleSubmit(async ({ until }) => {
    if (await action.run({ until })) onOpenChange(false)
  })

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title={copy.title} description={copy.help}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <DateField
          label={copy.field}
          min={min}
          max={max || undefined}
          hint={max ? rangeMessage : undefined}
          error={form.formState.errors.until?.message}
          {...form.register('until')}
        />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          <Button type="submit" isLoading={action.isPending}>
            {copy.confirm}
          </Button>
        </div>
      </form>
    </DialogShell>
  )
}
